<template>
  <div class="main-content">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-content">
        <div class="spinner-container">
          <div class="spinner"></div>
        </div>
        <h3>Importing Medications...</h3>
        <div class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <p class="progress-text">{{ uploadProgress }}% uploaded</p>
        </div>
        <div class="time-info">
          <p><strong>Elapsed Time:</strong> {{ formatTime(elapsedTime) }}</p>
          <p v-if="estimatedTimeRemaining > 0"><strong>Estimated Time Remaining:</strong> {{ formatTime(estimatedTimeRemaining) }}</p>
        </div>
        <p class="loading-message">{{ loadingMessage }}</p>
      </div>
    </div>

    <!-- Pharmacy Selection Modal -->
    <div v-if="showPharmacyModal" class="modal-overlay" @click="closePharmacyModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h4>{{ modalAction === 'export' ? 'Export Medications' : 'Import Medications' }}</h4>
          <button @click="closePharmacyModal" class="modal-close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Please select a pharmacy:</p>
          <select v-model.number="selectedPharmacyId" class="form-control pharmacy-select" @change="onPharmacySelect">
            <option :value="null">-- Select Pharmacy --</option>
            <option v-for="pharmacy in pharmacies" :key="pharmacy.id" :value="pharmacy.id">
              {{ pharmacy.name_en || pharmacy.name_ar || `Pharmacy #${pharmacy.id}` }}
            </option>
          </select>
        </div>
        <div class="modal-footer">
          <button @click="closePharmacyModal" class="btn btn-secondary">Cancel</button>
          <button @click="confirmPharmacySelection" class="btn btn-primary" :disabled="!selectedPharmacyId">
            {{ modalAction === 'export' ? 'Export' : 'Import' }}
          </button>
        </div>
      </div>
    </div>

    <div class="wrap-content container" id="container">
      <!-- start: PAGE TITLE -->
      <section id="page-title" style="background-color: #d1d1d1;">
        <div class="row">
          <div class="col-sm-10">
            <h1 class="mainTitle">All Pharmacy Medication</h1>
          </div>
          <div class="col-sm-2">
            <button @click="handleExportClick" class="btn btn-info" style="margin-right: 10px">
              Export Medicines
            </button>
            <button @click="handleImportClick" :disabled="isLoading" class="btn btn-warning" style="margin-right: 10px">
              Import Medicines
            </button>
            <input 
              ref="fileInput" 
              type="file" 
              accept=".xlsx,.xls,.csv" 
              @change="importMedications" 
              style="display: none"
              :disabled="isLoading"
            />
            <router-link v-if="hasPermission('medication-pharmacy create')" style="margin-right: 10px"
              class="btn btn-primary" :to="{ name: 'create-pharmacyMedication' }">create</router-link>
            <router-link v-if="hasPermission('medication-pharmacy getSoftDeleted')" class="btn btn-success"
              :to="{ name: 'deleted-pharmacyMedications' }">Deleted
              Medications</router-link>
          </div>

        </div>
      </section>
      <!-- end: PAGE TITLE -->
      <!-- start: DYNAMIC TABLE -->
      <div class="container-fluid container-fullw bg-white">
        <div class="row">
          <div class="col-md-12">

            <div v-if="medications.length > 0">
              <MyTable :columns="columns" :actions="true">
                <tr v-for="(medication, index) in filteredMedications" :key="index">
                  <th scope="row">{{ medication.id }}</th>
                  <td>{{ medication.pharmacy ? (medication.pharmacy.name_en || medication.pharmacy.name) : 'N/A' }}</td>
                  <!-- <td>{{ (medication.medication && medication.medication.category)? medication.medication.category.name_en : 'N/A' }}</td> -->

                   <td>{{ medication.medication ? (medication.medication.name_en || medication.medication.name) : 'N/A' }}</td>
                  <td>{{ medication.price ?? 'N/A' }}</td>
                  <td>{{ medication.price_after_discount ?? 'N/A' }}</td>
                  <td>{{ medication.quantity ?? 'N/A' }}</td>
                  <td>{{ medication.availability ? 'True' : 'False' }}</td>
                  <td>
                    <button class="me-2 btn btn-danger" v-if="hasPermission('medication-pharmacy delete')"
                      @click="deleteMedcation(medication.id)">
                      Delete
                    </button>
                    <router-link class="btn btn-primary spacing" v-if="hasPermission('medication-pharmacy update')"
                      :to="{ name: 'edit-pharmacyMedication', params: { id: medication.id } }">
                      Edit
                    </router-link>
                  </td>
                </tr>
              </MyTable>
            </div>
            <div v-else class="alert alert-info">
              No Pharmacy Medications found.
            </div>
          </div>
        </div>
      </div>
      <!-- end: DYNAMIC TABLE -->
    </div>
  </div>
</template>

<script>
import $ from 'jquery'; // Import jQuery
import { store } from "../../Store";
import MyTable from "../MyTable//MyTable.vue"; // Import your MyTable component
import axios from "../../Axios";
export default {
  name: "AllPharmacyMedications",
  components: {
    MyTable,
    // Paginate,
  },
  data() {
    return {
      dataTable: [],
      medications: [],
      meta: {},
      searchTerm: "",
      pharmacyId: null,
      isLoading: false,
      uploadProgress: 0,
      elapsedTime: 0,
      estimatedTimeRemaining: 0,
      loadingMessage: "Preparing file upload...",
      uploadStartTime: null,
      progressInterval: null,
      timeInterval: null,
      // Modal state
      showPharmacyModal: false,
      modalAction: 'export', // 'export' or 'import'
      selectedPharmacyId: null,
      pharmacies: [],
      pendingFile: null, // Store file when import is triggered
      columns: [
        { label: '#', key: 'id' },
        { label: 'Pharmacy Name', key: 'pharmacy.name_en' },
  
        { label: 'Medication Name', key: 'medication.name_en' },
        { label: 'Price', key: 'price' },
        { label: 'Price After Discount', key: 'price_after_discount' },
        { label: 'Quantity', key: 'quantity' },
        { label: 'Availability', key: 'availability' },
      ],
    };
  },

  async created() {
    this.fetchMedications();
    // Fetch pharmacies if admin user
    if (this.isAdminUser) {
      this.fetchPharmacies();
    }
  },

  beforeUnmount() {
    // Clean up intervals
    this.clearIntervals();
  },

  computed: {
    hasPermission() {
      return (permission) => store.hasPermission(permission);
    },
    isAdminUser() {
      // Check if user is not a pharmacy user
      const role = store.userRole ? store.userRole.toLowerCase().trim() : '';
      return role !== 'pharmacy';
    },
    filteredMedications() {
      if (!this.searchTerm) {
        return this.medications;
      }
      const term = this.searchTerm.toLowerCase();
      return this.medications.filter(
        (medication) => {
          const pharmacyName = medication.pharmacy ? (medication.pharmacy.name_en || medication.pharmacy.name || '').toLowerCase() : '';
          const medicationName = medication.medication ? (medication.medication.name_en || medication.medication.name || '').toLowerCase() : '';
          return pharmacyName.includes(term) || medicationName.includes(term);
        }
      );
    },
  },

  methods: {
    // Clear intervals
    clearIntervals() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
      if (this.timeInterval) {
        clearInterval(this.timeInterval);
        this.timeInterval = null;
      }
    },

    // Format time in MM:SS format
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },

    // Start timer
    startTimer() {
      this.elapsedTime = 0;
      this.uploadStartTime = Date.now();
      this.timeInterval = setInterval(() => {
        this.elapsedTime = Math.floor((Date.now() - this.uploadStartTime) / 1000);
        
        // Calculate estimated time remaining based on progress
        if (this.uploadProgress > 0 && this.uploadProgress < 100) {
          const timePerPercent = this.elapsedTime / this.uploadProgress;
          const remainingPercent = 100 - this.uploadProgress;
          this.estimatedTimeRemaining = Math.floor(timePerPercent * remainingPercent);
        }
      }, 1000);
    },

    // Handle pharmacy selection change
    onPharmacySelect() {
      const selectedId = this.selectedPharmacyId; // v-model.number already updated it
      console.log("Pharmacy selected from dropdown:", selectedId);
      console.log("Type of selected ID:", typeof selectedId);
      
      // Find the selected pharmacy object for logging
      const selectedPharmacy = this.pharmacies.find(p => p.id === selectedId);
      console.log("Selected pharmacy object:", selectedPharmacy);
      console.log("Pharmacy ID in object:", selectedPharmacy?.id);
    },

    // Fetch pharmacies list
    async fetchPharmacies() {
      try {
        const response = await axios.get(
          `/admin/admin/pharmacy/all`
        );
        
        // Handle both response structures
        const pharmaciesData = response.data.data || response.data;
        this.pharmacies = Array.isArray(pharmaciesData) ? pharmaciesData : [];
        
        // Debug: Log the pharmacy structure to see what fields are available
        if (this.pharmacies.length > 0) {
          console.log("Sample pharmacy object:", this.pharmacies[0]);
          console.log("Pharmacy ID field:", this.pharmacies[0].id);
          console.log("Pharmacy ID type:", typeof this.pharmacies[0].id);
        }
        
        console.log("Pharmacies fetched:", this.pharmacies.length);
      } catch (error) {
        console.error("Error fetching pharmacies:", error);
        this.pharmacies = [];
      }
    },

    // Get pharmacy ID from medications or user
    getPharmacyId() {
      // If admin and pharmacy is selected in modal, use that
      if (this.isAdminUser && this.selectedPharmacyId) {
        // Ensure it's a number if the API expects a number
        const pharmacyId = typeof this.selectedPharmacyId === 'string' 
          ? parseInt(this.selectedPharmacyId) 
          : this.selectedPharmacyId;
        console.log("Using selected pharmacy ID (admin):", pharmacyId, "Type:", typeof pharmacyId);
        return pharmacyId;
      }
      
      // Try to get from first medication
      if (this.medications.length > 0 && this.medications[0].pharmacy) {
        const id = this.medications[0].pharmacy.id;
        console.log("Using pharmacy ID from medications:", id);
        return id;
      }
      // Try to get from user if pharmacy user
      if (store.user && store.user.pharmacy_id) {
        console.log("Using pharmacy_id from user:", store.user.pharmacy_id);
        return store.user.pharmacy_id;
      }
      // Try to get from user id if it's the pharmacy id
      if (store.user && store.user.id) {
        console.log("Using user.id as pharmacy ID:", store.user.id);
        return store.user.id;
      }
      console.log("No pharmacy ID found");
      return null;
    },

    // Handle export button click
    handleExportClick() {
      if (this.isAdminUser) {
        // Show modal for admin
        this.modalAction = 'export';
        this.selectedPharmacyId = null;
        this.showPharmacyModal = true;
      } else {
        // Direct export for pharmacy users
        this.exportMedications();
      }
    },

    // Handle import button click
    handleImportClick() {
      if (this.isAdminUser) {
        // Show modal for admin
        this.modalAction = 'import';
        this.selectedPharmacyId = null;
        this.showPharmacyModal = true;
      } else {
        // Direct file input for pharmacy users
        this.triggerFileInput();
      }
    },

    // Close pharmacy modal
    closePharmacyModal() {
      this.showPharmacyModal = false;
      // Don't clear selectedPharmacyId here if we're in the middle of an operation
      // Only clear it after the operation is complete
      if (this.modalAction === 'export') {
        // For export, we'll clear it after export completes
      } else {
        this.selectedPharmacyId = null;
      }
      this.pendingFile = null;
    },

    // Confirm pharmacy selection
    confirmPharmacySelection() {
      if (!this.selectedPharmacyId) {
        alert('Please select a pharmacy');
        return;
      }

      // Store the selected pharmacy ID before closing modal
      const pharmacyIdToUse = this.selectedPharmacyId;
      
      // Close modal but keep the selected ID
      this.showPharmacyModal = false;
      this.pendingFile = null;

      if (this.modalAction === 'export') {
        // Use the stored pharmacy ID
        this.exportMedicationsWithId(pharmacyIdToUse);
      } else if (this.modalAction === 'import') {
        // Store the pharmacy ID for import
        this.selectedPharmacyId = pharmacyIdToUse;
        // Trigger file input after pharmacy selection
        this.$nextTick(() => {
          this.$refs.fileInput.click();
        });
      }
    },
    
    // Export medications with specific pharmacy ID
    async exportMedicationsWithId(pharmacyId) {
      try {
        console.log("Export - Pharmacy ID:", pharmacyId, "Type:", typeof pharmacyId);
        
        if (!pharmacyId) {
          alert('Pharmacy ID not found. Please select a pharmacy.');
          return;
        }

        const apiUrl = `/api/export/pharmacy/${pharmacyId}/medications`;
        console.log("Export API URL:", apiUrl);

        const response = await axios.get(
          apiUrl,
          {
            responseType: 'blob', // Important for file download
          }
        );

        // Create a blob from the response
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Get filename from response headers or use default
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'pharmacy_medications.xlsx';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        link.remove();
        window.URL.revokeObjectURL(url);
        
        // Clear selected pharmacy ID after successful export
        if (this.isAdminUser) {
          this.selectedPharmacyId = null;
        }
        
        alert('Medications exported successfully!');
      } catch (error) {
        console.error('Error exporting medications:', error);
        console.error('Error response:', error.response);
        alert('Error exporting medications. Please try again.');
        // Clear selected pharmacy ID on error too
        if (this.isAdminUser) {
          this.selectedPharmacyId = null;
        }
      }
    },
    
    // Export medications
    async exportMedications() {
      try {
        const pharmacyId = this.getPharmacyId();

           if (!pharmacyId) {
              alert('Pharmacy ID not found. Please select a pharmacy.');
              return;
            }

        console.log("Export - Pharmacy ID:", pharmacyId, "Type:", typeof pharmacyId);
        
        if (!pharmacyId) {
          alert('Pharmacy ID not found. Please select a pharmacy.');
          return;
        }

     
        const apiUrl = `/api/export/pharmacy/${pharmacyId}/medications`;
          console.log("Export API URL:", apiUrl);
 

        const response = await axios.get(
          apiUrl,
          {
            responseType: 'blob', // Important for file download
          }
        );

        // Create a blob from the response
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Get filename from response headers or use default
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'pharmacy_medications.xlsx';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        link.remove();
        window.URL.revokeObjectURL(url);
        
        alert('Medications exported successfully!');
      } catch (error) {
        console.error('Error exporting medications:', error);
        console.error('Error response:', error.response);
        alert('Error exporting medications. Please try again.');
      }
    },

    // Trigger file input click
    triggerFileInput() {
      if (!this.isLoading) {
        this.$refs.fileInput.click();
      }
    },

    // Import medications
    async importMedications(event) {
      try {
        const file = event.target.files[0];
        if (!file) {
          return;
        }

        // If admin, ensure pharmacy is selected
        if (this.isAdminUser && !this.selectedPharmacyId) {
          // This shouldn't happen, but just in case
          alert('Please select a pharmacy first');
          event.target.value = '';
          return;
        }

        const pharmacyId = this.getPharmacyId();
        console.log("Import - Pharmacy ID:", pharmacyId, "Type:", typeof pharmacyId);
        
        if (!pharmacyId) {
          alert('Pharmacy ID not found. Please select a pharmacy.');
          event.target.value = '';
          return;
        }

        // Validate file type
        const validTypes = [
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
          'application/vnd.ms-excel', // .xls
          'text/csv' // .csv
        ];
        
        if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
          alert('Please select a valid file (Excel or CSV format)');
          event.target.value = '';
          return;
        }

        // Initialize loading state
        this.isLoading = true;
        this.uploadProgress = 0;
        this.elapsedTime = 0;
        this.estimatedTimeRemaining = 0;
        this.loadingMessage = "Uploading file...";
        this.startTimer();

        const formData = new FormData();
        formData.append('file', file);

        const apiUrl = `/api/upload/pharmacy/${pharmacyId}/medications`;
        console.log("Import API URL:", apiUrl);

        const response = await axios.post(
          apiUrl,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              // Calculate upload progress
              if (progressEvent.total) {
                this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                this.loadingMessage = `Uploading file... ${this.uploadProgress}%`;
              }
            },
          }
        );

        // Upload complete, now processing
        this.uploadProgress = 100;
        this.loadingMessage = "Processing medications... Please wait...";

        console.log('Import response:', response.data);
        
        // Small delay to show completion
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Stop timer and clear intervals
        this.clearIntervals();
        
        // Hide loading
        this.isLoading = false;
        this.uploadProgress = 0;
        
        alert('Medications imported successfully!');
        
        // Refresh the medications list
        await this.fetchMedications();
        
        // Reset file input and selected pharmacy
        event.target.value = '';
        if (this.isAdminUser) {
          this.selectedPharmacyId = null;
        }
      } catch (error) {
        console.error('Error importing medications:', error);
        console.error('Error response:', error.response);
        
        // Stop timer and clear intervals
        this.clearIntervals();
        this.isLoading = false;
        this.uploadProgress = 0;
        
        const errorMessage = error.response?.data?.message || 'Error importing medications. Please check the file format and try again.';
        alert(errorMessage);
        event.target.value = '';
        if (this.isAdminUser) {
          this.selectedPharmacyId = null;
        }
      }
    },

    // fetch orders
    async fetchMedications() {
      try {
        const response = await axios.get(
          `/admin/admin/medication-pharmacy/all?is_paginate=0`
        );
        
        // Handle both response structures: response.data.data or response.data
        const medicationsData = response.data.data || response.data;
        
        // Ensure it's an array
        this.medications = Array.isArray(medicationsData) ? medicationsData : [];
        
        // Extract pharmacy ID from first medication if available
        if (this.medications.length > 0 && this.medications[0].pharmacy) {
          this.pharmacyId = this.medications[0].pharmacy.id;
        }
        
        // Set meta if available
        if (response.data.meta) {
          this.meta = response.data.meta;
        }
        
        console.log("Medications fetched:", this.medications.length, this.medications);
        console.log("Pharmacy ID:", this.pharmacyId);
        
        this.$nextTick(() => {
          this.initializeDataTable(); // Initialize DataTable after fetching users
        });
      } catch (error) {
        console.error("Error fetching medications:", error);
        this.medications = []; // Set to empty array on error
      }
    },
    initializeDataTable() {
      this.$nextTick(() => {
        // Ensure the DataTable is initialized only after the DOM is updated
        $('.sample_1').DataTable(); // Initialize DataTable
      });
    },
    deleteMedcation(id) {
      axios
        .delete(`/admin/admin/medication-pharmacy/delete?id=${id}`)
        .then((response) => {
          console.log(response.data);
          this.fetchMedications();
        })
        .catch((error) => {
          console.error(error);
        });
    },
  },

};
</script>
<style scoped>
.table {
  margin-top: 10px;
}

.spacing {
  margin-left: 10px !important;
}

/* Loading Overlay Styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-content {
  background: white;
  padding: 40px;
  border-radius: 10px;
  text-align: center;
  min-width: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.spinner-container {
  margin-bottom: 20px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-content h3 {
  margin-bottom: 20px;
  color: #333;
}

.progress-container {
  margin: 20px 0;
}

.progress-bar {
  width: 100%;
  height: 25px;
  background-color: #f0f0f0;
  border-radius: 15px;
  overflow: hidden;
  margin-bottom: 10px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3498db, #2ecc71);
  transition: width 0.3s ease;
  border-radius: 15px;
}

.progress-text {
  font-size: 14px;
  color: #666;
  margin: 5px 0;
}

.time-info {
  margin: 20px 0;
  text-align: left;
  background: #f9f9f9;
  padding: 15px;
  border-radius: 5px;
}

.time-info p {
  margin: 8px 0;
  font-size: 14px;
  color: #555;
}

.loading-message {
  margin-top: 15px;
  font-size: 14px;
  color: #666;
  font-style: italic;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Pharmacy Selection Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.modal-content {
  background: white;
  border-radius: 10px;
  min-width: 400px;
  max-width: 500px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h4 {
  margin: 0;
  color: #333;
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.modal-close-btn:hover {
  background-color: #f0f0f0;
}

.modal-body {
  padding: 20px;
}

.modal-body p {
  margin-bottom: 15px;
  color: #555;
  font-size: 16px;
}

.pharmacy-select {
  width: 100%;
  padding: 15px 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
  min-height: 50px;
  line-height: 1.5;
  background-color: #fff;
  cursor: pointer;
  transition: border-color 0.2s;
}

.pharmacy-select:hover {
  border-color: #007bff;
}

.pharmacy-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.modal-footer .btn {
  padding: 8px 20px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  font-size: 14px;
}

.modal-footer .btn-secondary {
  background-color: #6c757d;
  color: white;
}

.modal-footer .btn-secondary:hover {
  background-color: #5a6268;
}

.modal-footer .btn-primary {
  background-color: #007bff;
  color: white;
}

.modal-footer .btn-primary:hover {
  background-color: #0056b3;
}

.modal-footer .btn-primary:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}
</style>